import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useAuth } from '../../core/context/AuthContext';

const Ingredients = () => {
  const MySwal = withReactContent(Swal);
  const { getUserRole } = useAuth();
  const userRole = getUserRole(); // 'Admin', 'Manager', 'Cashier'
  const canCRUD = userRole === 'Admin' || userRole === 'Manager';

  const [ingredients, setIngredients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Restaurant Inventory');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = add mode, id string = edit mode

  const [formData, setFormData] = useState({
    name: '',
    variant: '',
    category: '',
    unit: 'pieces',
    currentStock: 0,
    reorderLevel: 5,
    costPerUnit: 0,
    supplier: ''
  });

  const units = ["kg", "g", "liters", "ml", "pieces", "packets", "bottles", "cans", "boxes"];

  useEffect(() => {
    fetchIngredients();
    fetchCategories();
  }, []);

  // Reset category filter when tab changes
  useEffect(() => {
    setActiveCategory('all');
  }, [activeTab]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories");
      setCategories(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchIngredients = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/ingredients");
      setIngredients(res.data || []);
    } catch (err) {
      MySwal.fire('Error', 'Failed to fetch inventory items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const currentTabCategories = categories.filter(c => c.type === activeTab && c.isActive !== false);

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      name: '', variant: '',
      category: currentTabCategories.length > 0 ? currentTabCategories[0]._id : '',
      unit: 'pieces', currentStock: 0, reorderLevel: 5, costPerUnit: 0, supplier: ''
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item._id);
    setFormData({
      name: item.name,
      variant: item.variant || '',
      category: item.category?._id || '',
      unit: item.unit,
      currentStock: item.currentStock,
      reorderLevel: item.reorderLevel,
      costPerUnit: item.costPerUnit,
      supplier: item.supplier || ''
    });
    setShowModal(true);
  };

  const handleSaveIngredient = async (e) => {
    e.preventDefault();
    try {
      if (!formData.category) {
        return MySwal.fire('Error', `Please create a category first in ${activeTab}`, 'error');
      }
      if (editingItem) {
        await axios.put(`https://avastar-pos-backend.onrender.com/api/ingredients/${editingItem}`, formData);
        MySwal.fire('Updated!', 'Item updated successfully', 'success');
      } else {
        await axios.post('https://avastar-pos-backend.onrender.com/api/ingredients', formData);
        MySwal.fire('Added!', 'Item created successfully', 'success');
      }
      setShowModal(false);
      fetchIngredients();
    } catch (err) {
      MySwal.fire('Error', err.response?.data?.message || 'Failed to save item', 'error');
    }
  };

  const handleDeleteItem = (item) => {
    const displayName = item.variant ? `${item.name} - ${item.variant}` : item.name;
    MySwal.fire({
      title: 'Delete Item?',
      html: `<b>${displayName}</b> will be removed from inventory.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`https://avastar-pos-backend.onrender.com/api/ingredients/${item._id}`);
          MySwal.fire('Deleted!', 'Item removed from inventory.', 'success');
          fetchIngredients();
        } catch (err) {
          MySwal.fire('Error', 'Failed to delete item', 'error');
        }
      }
    });
  };

  const handleAddIn = (item) => {
    const displayName = item.variant ? `${item.name} - ${item.variant}` : item.name;
    MySwal.fire({
      title: `<span style="font-size: 17px;">Add Stock IN</span>`,
      html: `<div style="background:#f0f9ff;padding:12px 16px;border-radius:10px;margin-bottom:12px;text-align:left;">
               <b style="color:#1e293b;font-size:15px;">${displayName}</b><br/>
               <span style="color:#64748b;font-size:13px;">Current stock: ${item.currentStock} ${item.unit}</span>
             </div>`,
      input: 'number',
      inputLabel: `How many ${item.unit} purchased / arrived?`,
      inputPlaceholder: `e.g. 10`,
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      confirmButtonText: '<i class="fas fa-arrow-down me-1"></i> Add to Stock',
      inputValidator: (value) => {
        if (!value || Number(value) <= 0) return 'Enter a positive quantity!';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.patch(`https://avastar-pos-backend.onrender.com/api/ingredients/${item._id}/stock`, {
            type: 'add', amount: Number(result.value)
          });
          MySwal.fire('Added!', `${result.value} ${item.unit} added to stock.`, 'success');
          fetchIngredients();
        } catch (err) {
          MySwal.fire('Error', 'Failed to add stock', 'error');
        }
      }
    });
  };

  const handleSetRemaining = (item) => {
    const displayName = item.variant ? `${item.name} - ${item.variant}` : item.name;
    MySwal.fire({
      title: `<span style="font-size: 17px;">End of Day Check</span>`,
      html: `<div style="background:#f0f9ff;padding:12px 16px;border-radius:10px;margin-bottom:12px;text-align:left;">
               <b style="color:#1e293b;font-size:15px;">${displayName}</b><br/>
               <span style="color:#64748b;font-size:13px;">System records: ${item.currentStock} ${item.unit}</span>
             </div>
             <p style="font-weight:600;font-size:14px;margin-bottom:6px;">How much is physically remaining?</p>`,
      input: 'number',
      inputPlaceholder: `Remaining ${item.unit}`,
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: '<i class="fas fa-calculator me-1"></i> Calculate OUT',
      inputValidator: (value) => {
        if (value === null || value === '' || Number(value) < 0) return 'Enter a valid amount (0 or more).';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.patch(`https://avastar-pos-backend.onrender.com/api/ingredients/${item._id}/stock`, {
            type: 'set_remaining', amount: Number(result.value)
          });
          const consumed = res.data.consumedAmount;
          MySwal.fire({
            icon: 'info',
            title: 'Done!',
            html: `<h4 style="color:#dc2626;font-weight:700;margin:12px 0;">OUT: ${consumed} ${item.unit}</h4>
                   <p style="color:#64748b;font-size:14px;">Remaining stock: ${result.value} ${item.unit}</p>`,
            confirmButtonText: 'OK',
            confirmButtonColor: '#10b981'
          });
          fetchIngredients();
        } catch (err) {
          MySwal.fire('Error', 'Failed to update stock', 'error');
        }
      }
    });
  };

  const handleViewStats = async (item) => {
    const displayName = item.variant ? `${item.name} - ${item.variant}` : item.name;
    try {
      const res = await axios.get(`https://avastar-pos-backend.onrender.com/api/ingredients/${item._id}/stats`);
      const { totalToday, totalWeek, totalMonth } = res.data;
      MySwal.fire({
        title: `<span style="font-size: 16px;font-weight:700;">Usage Stats</span>`,
        html: `
          <div style="color:#0f172a;font-weight:600;font-size:14px;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #f1f5f9;">${displayName}</div>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <div style="display:flex;justify-content:space-between;align-items:center;padding:14px 16px;background:#f0f9ff;border-radius:12px;">
              <span style="color:#64748b;font-weight:600;font-size:13px;"><i class="fas fa-calendar-day" style="color:#3b82f6;margin-right:8px;"></i>Today</span>
              <span style="font-size:22px;font-weight:800;color:#0f172a;">${totalToday} <span style="font-size:13px;font-weight:500;color:#64748b;">${item.unit}</span></span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;padding:14px 16px;background:#f0fdf4;border-radius:12px;">
              <span style="color:#64748b;font-weight:600;font-size:13px;"><i class="fas fa-calendar-week" style="color:#22c55e;margin-right:8px;"></i>This Week</span>
              <span style="font-size:22px;font-weight:800;color:#0f172a;">${totalWeek} <span style="font-size:13px;font-weight:500;color:#64748b;">${item.unit}</span></span>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;padding:14px 16px;background:#fef3c7;border-radius:12px;">
              <span style="color:#64748b;font-weight:600;font-size:13px;"><i class="fas fa-calendar-alt" style="color:#f59e0b;margin-right:8px;"></i>This Month</span>
              <span style="font-size:22px;font-weight:800;color:#0f172a;">${totalMonth} <span style="font-size:13px;font-weight:500;color:#64748b;">${item.unit}</span></span>
            </div>
          </div>
        `,
        confirmButtonText: 'Close',
        confirmButtonColor: '#6c757d',
        width: '360px'
      });
    } catch (err) {
      MySwal.fire('Error', 'Failed to fetch stats', 'error');
    }
  };

  const getGroupedItems = () => {
    const list = ingredients.filter(ing =>
      ing.category &&
      ing.category.type === activeTab &&
      ing.isActive !== false &&
      (activeCategory === 'all' || ing.category.name === activeCategory)
    );
    const groups = {};
    list.forEach(ing => {
      const catName = ing.category.name;
      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(ing);
    });
    return groups;
  };

  const groupedItems = getGroupedItems();

  // All unique category names available in this tab
  const tabCategoryNames = [
    ...new Set(
      ingredients
        .filter(ing => ing.category && ing.category.type === activeTab && ing.isActive !== false)
        .map(ing => ing.category.name)
    )
  ].sort();

  // â”€â”€â”€ Styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const togglerWrap = {
    display: 'flex', width: '100%', background: '#f1f5f9',
    borderRadius: '14px', padding: '4px', gap: '4px'
  };
  const tabBtn = (active) => ({
    flex: 1, border: 'none', borderRadius: '10px', padding: '11px 8px',
    fontWeight: 700, fontSize: '14px', cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: active ? '#4361ee' : 'transparent',
    color: active ? '#fff' : '#64748b',
    boxShadow: active ? '0 2px 8px rgba(67,97,238,0.25)' : 'none',
    whiteSpace: 'nowrap'
  });
  const inputStyle = {
    width: '100%', padding: '10px 14px',
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    fontSize: '14px', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    display: 'block', fontWeight: 600, fontSize: '11px',
    color: '#64748b', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  };
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* â”€â”€ Page Header â”€â”€ */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
            <h4 style={{ fontWeight: 700, margin: 0, fontSize: '1.3rem', color: '#1e293b' }}>
              Inventory Management
            </h4>
            {canCRUD && (
              <button onClick={openAddModal} style={{
                border: 'none', borderRadius: '10px', padding: '9px 18px',
                background: '#4361ee', color: '#fff', fontWeight: 700, fontSize: '14px',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(67,97,238,0.3)',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <i className="fas fa-plus"></i> New Item
              </button>
            )}
          </div>

          {/* Restaurant / Hotel Toggler */}
          <div style={togglerWrap}>
            <button style={tabBtn(activeTab === 'Restaurant Inventory')} onClick={() => setActiveTab('Restaurant Inventory')}>
              <i className="fas fa-utensils" style={{ marginRight: '7px' }}></i>Restaurant
            </button>
            <button style={tabBtn(activeTab === 'Hotel Inventory')} onClick={() => setActiveTab('Hotel Inventory')}>
              <i className="fas fa-bed" style={{ marginRight: '7px' }}></i>Hotel
            </button>
          </div>

          {/* Category Filter Pills */}
          {tabCategoryNames.length > 0 && (
            <div style={{
              display: 'flex', gap: '8px',
              overflowX: 'auto', paddingBottom: '4px', marginTop: '12px',
              scrollbarWidth: 'none', msOverflowStyle: 'none'
            }}>
              {/* All pill */}
              <button
                onClick={() => setActiveCategory('all')}
                style={{
                  flexShrink: 0, border: 'none', borderRadius: '20px',
                  padding: '7px 16px', fontWeight: 700, fontSize: '13px',
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.18s',
                  background: activeCategory === 'all'
                    ? 'linear-gradient(135deg, #4361ee, #3151de)'
                    : '#f1f5f9',
                  color: activeCategory === 'all' ? '#fff' : '#64748b',
                  boxShadow: activeCategory === 'all' ? '0 2px 8px rgba(67,97,238,0.3)' : 'none',
                }}
              >
                All
              </button>

              {tabCategoryNames.map(catName => (
                <button
                  key={catName}
                  onClick={() => setActiveCategory(catName)}
                  style={{
                    flexShrink: 0, border: 'none', borderRadius: '20px',
                    padding: '7px 16px', fontWeight: 700, fontSize: '13px',
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.18s',
                    background: activeCategory === catName
                      ? 'linear-gradient(135deg, #4361ee, #3151de)'
                      : '#f1f5f9',
                    color: activeCategory === catName ? '#fff' : '#64748b',
                    boxShadow: activeCategory === catName ? '0 2px 8px rgba(67,97,238,0.3)' : 'none',
                  }}
                >
                  {catName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* â”€â”€ Content â”€â”€ */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}></i>
            Loading...
          </div>
        ) : Object.keys(groupedItems).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fas fa-boxes" style={{ fontSize: '3.5rem', color: '#d1d5db', display: 'block', marginBottom: '16px' }}></i>
            <h5 style={{ color: '#6b7280', fontWeight: 700 }}>No Items Found</h5>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>
              {canCRUD
                ? 'Add categories in Category Management, then add items using the "New Item" button.'
                : 'No items have been added to this section yet.'}
            </p>
          </div>
        ) : (
          <div>
            {Object.keys(groupedItems).sort().map(categoryName => (
              <div key={categoryName} style={{
                marginBottom: '20px', background: '#fff',
                borderRadius: '16px', overflow: 'hidden',
                boxShadow: '0 1px 8px rgba(0,0,0,0.06)'
              }}>
                {/* Category Header */}
                <div style={{ padding: '13px 18px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-tag" style={{ color: '#4361ee', fontSize: '13px' }}></i>
                  <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '15px' }}>{categoryName}</span>
                  <span style={{ marginLeft: 'auto', background: '#eff6ff', color: '#3b82f6', borderRadius: '20px', fontSize: '12px', fontWeight: 700, padding: '2px 10px' }}>
                    {groupedItems[categoryName].length}
                  </span>
                </div>

                {groupedItems[categoryName].map((item, idx) => (
                  <div key={item._id} style={{
                    padding: '14px 18px',
                    borderBottom: idx < groupedItems[categoryName].length - 1 ? '1px solid #f8fafc' : 'none',
                  }}>

                    {/* Item Name + Variant Badge + Stock */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '10px' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', lineHeight: 1.3 }}>
                          {item.name}
                        </div>
                        {item.variant && (
                          <div style={{ marginTop: '5px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              background: 'linear-gradient(135deg, #6366f1 0%, #4361ee 100%)',
                              color: '#fff', borderRadius: '20px',
                              fontSize: '11px', fontWeight: 700,
                              padding: '3px 10px',
                              letterSpacing: '0.2px',
                              boxShadow: '0 1px 4px rgba(99,102,241,0.3)'
                            }}>
                              <i className="fas fa-layer-group" style={{ fontSize: '9px' }}></i>
                              {item.variant}
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <span style={{
                          display: 'inline-block', padding: '5px 12px',
                          borderRadius: '20px', fontSize: '13px', fontWeight: 700,
                          background: item.currentStock <= item.reorderLevel ? '#fef2f2' : '#f0fdf4',
                          color: item.currentStock <= item.reorderLevel ? '#dc2626' : '#16a34a',
                          border: `1px solid ${item.currentStock <= item.reorderLevel ? '#fecaca' : '#bbf7d0'}`
                        }}>
                          {item.currentStock} {item.unit}
                          {item.currentStock <= item.reorderLevel && (
                            <i className="fas fa-exclamation-triangle" style={{ marginLeft: '5px', fontSize: '10px' }}></i>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>

                      {/* Stats - visible to all */}
                      <button onClick={() => handleViewStats(item)} style={{
                        flex: '1 1 55px', minWidth: '55px', padding: '8px 4px',
                        border: '1.5px solid #e0e7ff', borderRadius: '9px',
                        background: '#fff', color: '#4361ee', fontWeight: 700,
                        fontSize: '12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                      }}>
                        <i className="fas fa-chart-bar"></i> Stats
                      </button>

                      {/* IN - visible to all */}
                      <button onClick={() => handleAddIn(item)} style={{
                        flex: '1 1 55px', minWidth: '55px', padding: '8px 4px',
                        border: '1.5px solid #bfdbfe', borderRadius: '9px',
                        background: '#eff6ff', color: '#2563eb', fontWeight: 700,
                        fontSize: '12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                      }}>
                        <i className="fas fa-arrow-down"></i> IN
                      </button>

                      {/* Calc OUT - visible to all */}
                      <button onClick={() => handleSetRemaining(item)} style={{
                        flex: '1.5 1 85px', minWidth: '85px', padding: '8px 4px',
                        border: 'none', borderRadius: '9px',
                        background: 'linear-gradient(135deg, #4361ee, #3151de)',
                        color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        boxShadow: '0 2px 6px rgba(67,97,238,0.3)'
                      }}>
                        <i className="fas fa-calculator"></i> Calc OUT
                      </button>

                      {/* Edit & Delete - Admin / Manager only */}
                      {canCRUD && (
                        <>
                          <button onClick={() => openEditModal(item)} style={{
                            flex: '0 0 36px', width: '36px', padding: '8px 0',
                            border: '1.5px solid #d1fae5', borderRadius: '9px',
                            background: '#f0fdf4', color: '#059669', fontWeight: 700,
                            fontSize: '13px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }} title="Edit Item">
                            <i className="fas fa-pen"></i>
                          </button>
                          <button onClick={() => handleDeleteItem(item)} style={{
                            flex: '0 0 36px', width: '36px', padding: '8px 0',
                            border: '1.5px solid #fecaca', borderRadius: '9px',
                            background: '#fef2f2', color: '#dc2626', fontWeight: 700,
                            fontSize: '13px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }} title="Delete Item">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* â”€â”€ Add / Edit Item Modal â”€â”€ */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,23,42,0.55)', zIndex: 9999,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          padding: '0'
        }}>
          <div style={{
            background: '#fff', borderRadius: '24px 24px 0 0',
            width: '100%', maxWidth: '520px',
            maxHeight: '93vh', overflow: 'auto',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
            animation: 'slideUp 0.25s ease'
          }}>
            {/* Sheet Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px' }}>
              <div style={{ width: '40px', height: '4px', background: '#e2e8f0', borderRadius: '4px' }}></div>
            </div>

            {/* Modal Header */}
            <div style={{ padding: '10px 24px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 style={{ fontWeight: 700, margin: 0, fontSize: '18px', color: '#1e293b' }}>
                  {editingItem ? 'Edit Item' : 'New Item'}
                </h5>
                <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>
                  {activeTab}
                </div>
              </div>
              <button onClick={() => setShowModal(false)} style={{
                border: 'none', background: '#f1f5f9', borderRadius: '50%',
                width: '34px', height: '34px', cursor: 'pointer', color: '#64748b', fontSize: '15px'
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{ padding: '16px 24px 32px' }}>
              <form onSubmit={handleSaveIngredient}>

                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Item Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                    placeholder="e.g. Water Bottle, Chicken, Soap..."
                    style={inputStyle} />
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Variant / Specification</label>
                  <input type="text" name="variant" value={formData.variant} onChange={handleInputChange}
                    placeholder="e.g. 500ml Aquafina Â· Breast Boneless Â· Basmati 5kg"
                    style={inputStyle} />
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Brand, size, type, cut - makes this item specific & trackable
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Sub-Category *</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} required style={inputStyle}>
                    <option value="" disabled>Select category</option>
                    {currentTabCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Unit</label>
                    <select name="unit" value={formData.unit} onChange={handleInputChange} style={inputStyle}>
                      {units.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Initial Stock</label>
                    <input type="number" name="currentStock" value={formData.currentStock} onChange={handleInputChange} style={inputStyle} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Low Stock Alert</label>
                    <input type="number" name="reorderLevel" value={formData.reorderLevel} onChange={handleInputChange} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Cost / Unit</label>
                    <input type="number" name="costPerUnit" value={formData.costPerUnit} onChange={handleInputChange} style={inputStyle} />
                  </div>
                </div>

                <div style={{ marginBottom: '22px' }}>
                  <label style={labelStyle}>Supplier</label>
                  <input type="text" name="supplier" value={formData.supplier} onChange={handleInputChange}
                    placeholder="e.g. Lanka Dairies, Local Market"
                    style={inputStyle} />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{
                    flex: 1, padding: '13px', border: '1.5px solid #e2e8f0',
                    borderRadius: '12px', background: '#fff', fontWeight: 600,
                    fontSize: '14px', cursor: 'pointer', color: '#64748b'
                  }}>Cancel</button>
                  <button type="submit" style={{
                    flex: 2, padding: '13px', border: 'none', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #4361ee, #3151de)',
                    color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(67,97,238,0.35)'
                  }}>
                    <i className={`fas ${editingItem ? 'fa-save' : 'fa-check'} me-2`}></i>
                    {editingItem ? 'Save Changes' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Ingredients;
